import React from 'react';
import { cltw } from '../../utils';
import { Label } from '../Label/Label';

export type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

/**
 * @description Base input component.
 * @documentation https://ui.shadcn.com/docs/components/input
 */
export const BasicInput = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, ...props }, ref) => {
    return (
      <input
        className={cltw(
          'body-lg disabled:bg-gray-10 h-14 w-full rounded-md border px-[14px] text-base focus:outline-none focus:ring-2 dark:border-white/70 dark:bg-transparent dark:text-white',
          className,
        )}
        ref={ref}
        type={type}
        {...props}
      />
    );
  },
);
BasicInput.displayName = 'BasicInput';

/**
 * Internal floating input component. Do not export or use directly.
 */
const FloatingInput = React.forwardRef<HTMLInputElement, InputProps>(
  ({ ...props }, ref) => {
    return (
      <BasicInput
        className='peer pt-4 placeholder-transparent aria-[invalid=true]:border-red-500 aria-[invalid=true]:text-red-500 aria-[invalid=true]:ring-0'
        ref={ref}
        {...props}
      />
    );
  },
);
FloatingInput.displayName = 'FloatingInput';

/**
 * Floating label input component.
 * Adapted from the following implementation, but customized with our styles and components:
 * @documentation https://shadcnui-expansions.typeart.cc/docs/floating-label-input
 */
export type FloatingLabelInputProps = InputProps & {
  label: string;
  labelClassNames?: string;
};
export const Input = React.forwardRef<
  React.ElementRef<typeof FloatingInput>,
  React.PropsWithoutRef<FloatingLabelInputProps>
>(({ label, labelClassNames, id, ...props }, ref) => {
  return (
    <div className='relative'>
      <FloatingInput id={id} {...props} placeholder={label} ref={ref} />
      <Label
        className={cltw(
          'max-w-[calc(100%-28px)] overflow-hidden text-ellipsis whitespace-nowrap',
          labelClassNames,
        )}
        htmlFor={id}
        variant='placeholder'
      >
        {label}
      </Label>
    </div>
  );
});

Input.displayName = 'InputPrimary';
